import { useProfile } from '@/api/profile'
import type { AccessRole, RecordingScope } from '@/api/auth'

export interface Access {
  role: AccessRole
  isOwner: boolean
  /** Owner or manager: settings, delete and download recordings. */
  isManager: boolean
  isSupervisor: boolean
  recordings: RecordingScope
  canManageRecordings: boolean
  canBilling: boolean
  /** Team analytics and live calls (owner, managers, supervisors). */
  canSeeTeamInsights: boolean
  loaded: boolean
}

/**
 * What the signed-in person may do in their company. The server enforces
 * every rule; screens only hide what would be refused.
 *
 * Without `access` (older server), the role decides as before: a MANAGER
 * does everything, an AGENT is a member.
 */
export function useAccess(): Access {
  const { data: profile, isLoading } = useProfile()
  const access = profile?.access
  const role: AccessRole = access?.role ?? (profile?.role === 'MANAGER' ? 'owner' : 'member')
  const isManager = role === 'owner' || role === 'manager'
  return {
    role,
    isOwner: role === 'owner',
    isManager,
    isSupervisor: role === 'supervisor',
    recordings: access?.recordings ?? (isManager ? 'all' : 'own'),
    canManageRecordings: access?.manage_recordings ?? isManager,
    canBilling: access?.billing ?? isManager,
    canSeeTeamInsights: access?.team_insights ?? isManager,
    loaded: !isLoading,
  }
}
